import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Shield, 
  Lock, 
  ShieldCheck, 
  CheckCircle2, 
  Clock, 
  RefreshCw, 
  Smartphone 
} from 'lucide-react';

interface LiqPayInlineTerminalProps {
  totalPrice: number;
  currency?: string;
  guestPhone: string;
  guestName: string;
  onPaymentSuccess: (paymentId: string) => void;
  onPaymentCancel: () => void;
  onPaymentInitiated: () => { bookingId: string; price: number };
  theme?: 'light' | 'dark';
  currentLanguage?: string;
}

const TERMINAL_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    title: "LiqPay Secure Checkout",
    selectMethod: "Select Payment Option",
    paymentSummary: "Payable Amount",
    cardDetails: "Pay by card details",
    applePay: "Apple Pay",
    googlePay: "Google Pay",
    privat24: "Privat 24",
    payWithCard: "Pay with Card",
    cancel: "Cancel",
    back: "Back",
    waitingPayment: "Redirecting to secure gateway...",
    instruction1: "Connecting you to the secure LiqPay checkout...",
    instruction2: "Please complete your payment of ₴{amount} directly on the gateway window.",
    verifyingTitle: "Verifying Live Payment...",
    verifyStep1: "Connecting to LiqPay secure payment network...",
    verifyStep2: "Checking transaction status for ₴{amount}...",
    verifyStep3: "Verifying 3D-Secure response...",
    verifyStep4: "Finalizing payment confirmation...",
    verifySuccess: "Payment Verified Successfully!",
    redirecting: "Finalizing booking dispatch...",
    secureNotice: "Payments are processed directly via secure LiqPay protocol.",
    paymentOptionSubtitle1: "Pay securely using any Visa or Mastercard credit/debit card.",
    paymentOptionSubtitle2: "Quick checkout with Apple Pay on Apple devices.",
    paymentOptionSubtitle3: "Fast and secure checkout with Google Pay.",
    paymentOptionSubtitle4: "Pay directly via Privat24 internet banking."
  },
  uk: {
    title: "Безпечна оплата LiqPay",
    selectMethod: "Оберіть спосіб оплати",
    paymentSummary: "Сума до сплати",
    cardDetails: "Оплата платіжною карткою",
    applePay: "Apple Pay",
    googlePay: "Google Pay",
    privat24: "Privat 24",
    payWithCard: "Оплатити карткою",
    cancel: "Скасувати",
    back: "Назад",
    waitingPayment: "Перенаправлення на безпечний шлюз...",
    instruction1: "Підключення до безпечної сторінки оплати LiqPay...",
    instruction2: "Будь ласка, здійсніть оплату в розмірі ₴{amount} безпосередньо у вікні шлюзу.",
    verifyingTitle: "Перевірка платежу...",
    verifyStep1: "Підключення до платіжної мережі LiqPay...",
    verifyStep2: "Перевірка статусу транзакції на суму ₴{amount}...",
    verifyStep3: "Перевірка відповіді 3D-Secure...",
    verifyStep4: "Остаточне підтвердження платежу...",
    verifySuccess: "Платіж успішно підтверджено!",
    redirecting: "Завершення оформлення бронювання...",
    secureNotice: "Платежі обробляються безпосередньо через безпечний протокол LiqPay.",
    paymentOptionSubtitle1: "Безпечна оплата за допомогою будь-кої картки Visa або Mastercard.",
    paymentOptionSubtitle2: "Швидка оплата за допомогою Apple Pay на пристроях Apple.",
    paymentOptionSubtitle3: "Швидкий та безпечний розрахунок за допомогою Google Pay.",
    paymentOptionSubtitle4: "Сплачуйте безпосередньо через інтернет-банкінг Приват24."
  },
  de: {
    title: "LiqPay Sicheres Bezahlen",
    selectMethod: "Zahlungsoption auswählen",
    textSelectMethod: "Zahlungsmethode auswählen",
    paymentSummary: "Zahlungsbetrag",
    cardDetails: "Mit Kreditkarte bezahlen",
    applePay: "Apple Pay",
    googlePay: "Google Pay",
    privat24: "Privat 24",
    payWithCard: "Mit Karte zahlen",
    cancel: "Abbrechen",
    back: "Zurück",
    waitingPayment: "Weiterleitung zum sicheren Gateway...",
    instruction1: "Verbindung zur sicheren LiqPay-Zahlungsseite wird hergestellt...",
    instruction2: "Bitte schließen Sie Ihre Zahlung in Höhe von ₴{amount} direkt im Gateway-Fenster ab.",
    verifyingTitle: "Zahlung wird überprüft...",
    verifyStep1: "Verbindung zum sicheren LiqPay-Zahlungsnetzwerk...",
    verifyStep2: "Überprüfung des Transaktionsstatus für ₴{amount}...",
    verifyStep3: "Verifizierung der 3D-Secure-Antwort...",
    verifyStep4: "Zahlungsbestätigung wird abgeschlossen...",
    verifySuccess: "Zahlung erfolgreich verifiziert!",
    redirecting: "Buchung wird abgeschlossen...",
    secureNotice: "Zahlungen werden direkt über das sichere LiqPay-Protokoll verarbeitet.",
    paymentOptionSubtitle1: "Sicher bezahlen mit jeder Visa- oder Mastercard-Kredit-/Debitkarte.",
    paymentOptionSubtitle2: "Schneller Checkout mit Apple Pay auf Apple-Geräten.",
    paymentOptionSubtitle3: "Schneller und sicherer Checkout mit Google Pay.",
    paymentOptionSubtitle4: "Direkt über das Privat24 Internet-Banking bezahlen."
  },
  es: {
    title: "Pago Seguro LiqPay",
    selectMethod: "Seleccione Opción de Pago",
    paymentSummary: "Cantidad a Pagar",
    cardDetails: "Pago con tarjeta de crédito/débito",
    applePay: "Apple Pay",
    googlePay: "Google Pay",
    privat24: "Privat 24",
    payWithCard: "Pagar con tarjeta",
    cancel: "Cancelar",
    back: "Atrás",
    waitingPayment: "Redireccionando a la pasarela segura...",
    instruction1: "Conectando con la página de pago seguro de LiqPay...",
    instruction2: "Por favor, complete su pago de ₴{amount} directamente en la ventana de la pasarela.",
    verifyingTitle: "Verificando el pago en vivo...",
    verifyStep1: "Conectando con la red de pago seguro de LiqPay...",
    verifyStep2: "Comprobando el estado de la transacción para ₴{amount}...",
    verifyStep3: "Verificando la respuesta 3D-Secure...",
    verifyStep4: "Finalizando la confirmación del pago...",
    verifySuccess: "¡Pago verificado con éxito!",
    redirecting: "Finalizando el envío de la reserva...",
    secureNotice: "Los pagos se procesan directamente a través del protocolo seguro LiqPay.",
    paymentOptionSubtitle1: "Pague de forma segura con cualquier tarjeta de crédito o débito Visa o Mastercard.",
    paymentOptionSubtitle2: "Pago rápido con Apple Pay en dispositivos Apple.",
    paymentOptionSubtitle3: "Pago rápido y seguro con Google Pay.",
    paymentOptionSubtitle4: "Pague directamente a través de la banca por internet Privat24."
  },
  fr: {
    title: "Paiement Sécurisé LiqPay",
    selectMethod: "Sélectionnez le mode de paiement",
    paymentSummary: "Montant à payer",
    cardDetails: "Payer par carte de crédit/débit",
    applePay: "Apple Pay",
    googlePay: "Google Pay",
    privat24: "Privat 24",
    payWithCard: "Payer par carte",
    cancel: "Annuler",
    back: "Retour",
    waitingPayment: "Redirection vers la passerelle sécurisée...",
    instruction1: "Connexion à la page de paiement sécurisée LiqPay...",
    instruction2: "Veuillez effectuer votre paiement de ₴{amount} directement sur la fenêtre de la passerelle.",
    verifyingTitle: "Vérification du paiement en direct...",
    verifyStep1: "Connexion au réseau de paiement sécurisé LiqPay...",
    verifyStep2: "Vérification du statut de la transaction pour ₴{amount}...",
    verifyStep3: "Vérification de la réponse 3D-Secure...",
    verifyStep4: "Finalisation de la confirmation de paiement...",
    verifySuccess: "Paiement vérifié avec succès !",
    redirecting: "Finalisation de l'envoi de la réservation...",
    secureNotice: "Les paiements sont traités directement via le protocole sécurisé LiqPay.",
    paymentOptionSubtitle1: "Payez en toute sécurité avec n'importe quelle carte de crédit ou de débit Visa ou Mastercard.",
    paymentOptionSubtitle2: "Paiement rapide avec Apple Pay sur les appareils Apple.",
    paymentOptionSubtitle3: "Paiement rapide et sécurisé avec Google Pay.",
    paymentOptionSubtitle4: "Payez directement via la banque en ligne Privat24."
  }
};

function calculateSha1Base64(str: string): string {
  const buffer = new TextEncoder().encode(str);
  const words: number[] = [];
  const len = buffer.length;
  for (let i = 0; i < len; i++) {
    words[i >> 2] |= buffer[i] << (24 - (i % 4) * 8);
  }
  
  const totalBits = len * 8;
  words[totalBits >> 5] |= 0x80 << (24 - (totalBits % 32));
  
  const wordsLen = ((totalBits + 64 >>> 9) << 4) + 16;
  while (words.length < wordsLen) words.push(0);
  words[wordsLen - 1] = totalBits;
  words[wordsLen - 1] = totalBits;
  
  let h0 = 0x67452301;
  let h1 = 0xEFCDAB89;
  let h2 = 0x98BADCFE;
  let h3 = 0x10325476;
  let h4 = 0xC3D2E1F0;
  
  const w: number[] = new Array(80);
  for (let i = 0; i < words.length; i += 16) {
    for (let t = 0; t < 16; t++) {
      w[t] = words[i + t];
    }
    for (let t = 16; t < 80; t++) {
      const val = w[t - 3] ^ w[t - 8] ^ w[t - 14] ^ w[t - 16];
      w[t] = (val << 1) | (val >>> 31);
    }
    
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    
    for (let t = 0; t < 80; t++) {
      let f = 0;
      let k = 0;
      if (t < 20) {
        f = (b & c) | (~b & d);
        k = 0x5A827999;
      } else if (t < 40) {
        f = b ^ c ^ d;
        k = 0x6ED9EBA1;
      } else if (t < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8F1BBCDC;
      } else {
        f = b ^ c ^ d;
        k = 0xCA62C1D6;
      }
      
      const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[t]) | 0;
      e = d;
      d = c;
      c = (b << 30) | (b >>> 2);
      b = a;
      a = temp;
    }
    
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
  }
  
  const digestBytes = new Uint8Array(20);
  const hs = [h0, h1, h2, h3, h4];
  for (let i = 0; i < 5; i++) {
    digestBytes[i * 4] = (hs[i] >>> 24) & 0xff;
    digestBytes[i * 4 + 1] = (hs[i] >>> 16) & 0xff;
    digestBytes[i * 4 + 2] = (hs[i] >>> 8) & 0xff;
    digestBytes[i * 4 + 3] = hs[i] & 0xff;
  }
  
  const binaryString = Array.from(digestBytes).map(b => String.fromCharCode(b)).join('');
  return btoa(binaryString);
}

export function LiqPayInlineTerminal({
  totalPrice,
  currency = "UAH",
  guestPhone,
  guestName,
  onPaymentSuccess,
  onPaymentCancel,
  onPaymentInitiated,
  theme = 'light',
  currentLanguage = 'en'
}: LiqPayInlineTerminalProps) {
  const lang = ['uk', 'en', 'de', 'es', 'fr'].includes(currentLanguage || 'en') ? (currentLanguage || 'en') : 'en';
  const t = TERMINAL_TRANSLATIONS[lang];

  const [paymentOption, setPaymentOption] = useState<'none' | 'card' | 'apple' | 'google' | 'privat'>('none');
  const [step, setStep] = useState<'select' | 'verifying' | 'success'>('select');
  
  const [verificationIndex, setVerificationIndex] = useState(0);
  const [verificationSteps, setVerificationSteps] = useState<string[]>([]);
  
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  const [activePrice, setActivePrice] = useState<number>(totalPrice);
  const [activeData, setActiveData] = useState<string>('');
  const [activeSignature, setActiveSignature] = useState<string>('');
  const [gatewayUrl, setGatewayUrl] = useState<string>('');
  const [manualCheckError, setManualCheckError] = useState<string | null>(null);

  const isDark = theme === 'dark';

  const getHelperText = (key: string) => {
    const helperTexts: Record<string, Record<string, string>> = {
      en: {
        newTabOpened: "Secure Payment Window Opened",
        newTabInstructions: "Please complete your payment of ₴{amount} in the new window. This page will automatically update once payment is completed.",
        btnOpenGateway: "Proceed to LiqPay Payment",
        btnVerifyManual: "I have completed payment",
        waitingPaymentConfirm: "Waiting for payment confirmation...",
        noPaymentDetected: "Payment not detected yet. Please make sure you have finalized checkout in the payment window.",
        changeMethod: "Back to select method"
      },
      uk: {
        newTabOpened: "Відкрито вікно безпечної оплати",
        newTabInstructions: "Будь ласка, завершіть оплату в розмірі ₴{amount} у новому вікні. Ця сторінка автоматично оновиться після успішної оплати.",
        btnOpenGateway: "Перейти до оплати LiqPay",
        btnVerifyManual: "Я вже здійснив оплату",
        waitingPaymentConfirm: "Очікуємо на підтвердження оплати...",
        noPaymentDetected: "Оплату ще не виявлено. Будь ласка, переконайтеся, що ви завершили транзакцію у вікні оплати.",
        changeMethod: "Повернутися до вибору способу"
      },
      de: {
        newTabOpened: "Sicheres Zahlungsfenster geöffnet",
        newTabInstructions: "Bitte schließen Sie Ihre Zahlung in Höhe von ₴{amount} im neuen Fenster ab. Diese Seite wird nach erfolgreicher Zahlung automatisch aktualisiert.",
        btnOpenGateway: "Zur LiqPay-Zahlung wechseln",
        btnVerifyManual: "Ich habe die Zahlung abgeschlossen",
        waitingPaymentConfirm: "Warten auf Zahlungsbestätigung...",
        noPaymentDetected: "Zahlung noch nicht erkannt. Bitte stellen Sie sicher, dass Sie den Checkout im Zahlungsfenster abgeschlossen haben.",
        changeMethod: "Zurück zur Auswahl"
      },
      es: {
        newTabOpened: "Ventana de pago seguro abierta",
        newTabInstructions: "Por favor, complete su pago de ₴{amount} en la nueva ventana. Esta página se actualizará automáticamente una vez completado el pago.",
        btnOpenGateway: "Proceder al pago de LiqPay",
        btnVerifyManual: "He completado el pago",
        waitingPaymentConfirm: "Esperando confirmación de pago...",
        noPaymentDetected: "Pago no detectado todavía. Por favor, asegúrese de haber finalizado el pago en la ventana de pago.",
        changeMethod: "Volver a seleccionar método"
      },
      fr: {
        newTabOpened: "Fenêtre de paiement sécurisé ouverte",
        newTabInstructions: "Veuillez effectuer votre paiement de ₴{amount} dans la nouvelle fenêtre. Cette page se mettra à jour automatiquement une fois le paiement effectué.",
        btnOpenGateway: "Procéder au paiement LiqPay",
        btnVerifyManual: "J'ai effectué le paiement",
        waitingPaymentConfirm: "En attente de confirmation du paiement...",
        noPaymentDetected: "Paiement non détecté pour le moment. Veuillez vous assurer d'avoir finalisé le paiement dans la fenêtre de paiement.",
        changeMethod: "Retour à la sélection"
      }
    };
    const currentLangDict = helperTexts[lang] || helperTexts['en'];
    return currentLangDict[key] || helperTexts['en'][key];
  };

  const handleVerifyPayment = () => {
    setVerificationSteps([
      t.verifyStep1,
      t.verifyStep2.replace('{amount}', totalPrice.toLocaleString()),
      t.verifyStep3,
      t.verifyStep4
    ]);
    setVerificationIndex(0);
    setStep('verifying');
  };

  const checkPaymentStatus = (bookingIdToCheck: string | null = activeBookingId): boolean => {
    const targetId = bookingIdToCheck || activeBookingId;
    if (!targetId) return false;

    try {
      const savedRaw = localStorage.getItem('cozzydine_bookings_v21');
      if (savedRaw) {
        const bookingsList = JSON.parse(savedRaw);
        const booking = bookingsList.find((b: any) => b.id === targetId && b.status === 'confirmed');
        if (booking) {
          setStep('success');
          setTimeout(() => {
            onPaymentSuccess(booking.paymentId || 'PAY-LIQPAY-GATEWAY-SUCCESS');
          }, 1500);
          return true;
        }
      }
    } catch (e) {
      console.warn("Error reading localStorage in checkPaymentStatus:", e);
    }
    return false;
  };

  useEffect(() => {
    if (step !== 'verifying' || !activeBookingId) return;

    const detected = checkPaymentStatus(activeBookingId);
    if (detected) return;

    const intervalId = setInterval(() => {
      checkPaymentStatus(activeBookingId);
    }, 1000);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cozzydine_bookings_v21') {
        checkPaymentStatus(activeBookingId);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [step, activeBookingId]);

  const handleManualCheck = () => {
    setManualCheckError(null);
    const success = checkPaymentStatus();
    if (!success) {
      setManualCheckError(getHelperText('noPaymentDetected'));
      setTimeout(() => {
        setManualCheckError(null);
      }, 5000);
    }
  };

  const handleSelectOption = (option: 'card' | 'apple' | 'google' | 'privat') => {
    setPaymentOption(option);
    
    const { bookingId, price } = onPaymentInitiated();
    if (!bookingId) return;

    setActiveBookingId(bookingId);
    setActivePrice(price);

    const publicKey = 'sandbox_i36552116639';
    const privateKey = 'sandbox_i36552116639';
    const resultUrl = window.location.origin + '?payment_success=true&booking_id=' + bookingId + '&amount=' + price;

    const params = {
      public_key: publicKey,
      version: 3,
      action: 'pay',
      amount: price,
      currency: 'UAH',
      description: `Hostel Cozzydine Booking #${bookingId} for ${guestName}`,
      order_id: bookingId,
      result_url: resultUrl,
      sandbox: 1,
      language: lang
    };

    const jsonString = JSON.stringify(params);
    const utf8Bytes = new TextEncoder().encode(jsonString);
    const binaryString = Array.from(utf8Bytes).map(b => String.fromCharCode(b)).join('');
    const data = btoa(binaryString);

    const signature = calculateSha1Base64(privateKey + data + privateKey);

    setActiveData(data);
    setActiveSignature(signature);

    const targetUrl = `https://www.liqpay.ua/${lang === 'uk' ? 'uk' : 'en'}/checkout/i36552116639?amount=${price}&currency=UAH&description=${encodeURIComponent(`Hostel Cozzydine Booking #${bookingId} for ${guestName}`)}`;
    setGatewayUrl(targetUrl);

    setStep('verifying');
    setVerificationSteps([
      "Initializing secure payment request...",
      `Preparing transaction for ₴${price.toLocaleString()} UAH...`,
      "Redirecting to LiqPay gateway..."
    ]);
    setVerificationIndex(0);

    try {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.warn("Popup blocked by browser iframe policy, user can click the manual button in next step:", e);
    }
  };

  useEffect(() => {
    if (step === 'verifying' && verificationSteps.length > 0) {
      const interval = setInterval(() => {
        setVerificationIndex(prev => {
          if (prev >= verificationSteps.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 500);
      return () => clearInterval(interval);
    }
  }, [step, verificationSteps]);

  return (
    <div id="liqpay-inline-terminal" className={`p-6 sm:p-8 rounded-3xl border font-sans transition-all duration-300 ${
      isDark ? 'bg-zinc-950 border-zinc-800 text-zinc-100' : 'bg-white border-stone-200 text-stone-900'
    }`}>
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-200/50 dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500">
            <Shield className="w-5 h-5 text-amber-500" />
          </div>
          <span className="font-display font-black text-sm tracking-wide uppercase">{t.title}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-mono">
          <Lock className="w-3.5 h-3.5 text-emerald-500" />
          <span>SSL ENCRYPTED</span>
        </div>
      </div>

      {step === 'select' && (
        <div className="space-y-6">
          <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 p-4 rounded-2xl flex justify-between items-center">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{t.paymentSummary}</span>
            <span className="text-xl font-black font-mono text-zinc-900 dark:text-white">₴{totalPrice.toLocaleString()}</span>
          </div>

          <div className="space-y-3">
            <h3 className="text-xs font-black text-zinc-400 uppercase tracking-widest mb-2">{t.selectMethod}</h3>
            
            <button
              type="button"
              onClick={() => handleSelectOption('card')}
              className={`w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all hover:scale-[1.01] cursor-pointer ${
                isDark 
                  ? 'border-zinc-800 hover:border-amber-500/50 bg-zinc-900/40 text-zinc-100' 
                  : 'border-zinc-100 hover:border-amber-500/50 bg-stone-50/50 text-stone-900'
              }`}
            >
              <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-500 mt-0.5">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold block">{t.cardDetails}</span>
                <span className="text-[10px] text-zinc-400 block mt-0.5 leading-relaxed">{t.paymentOptionSubtitle1}</span>
              </div>
              <span className="text-[10px] font-mono font-bold text-amber-500 self-center">Visa/MC</span>
            </button>

            <button
              type="button"
              onClick={() => handleSelectOption('apple')}
              className={`w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all hover:scale-[1.01] cursor-pointer ${
                isDark 
                  ? 'border-zinc-800 hover:border-zinc-600 bg-black text-white' 
                  : 'border-zinc-100 hover:border-zinc-300 bg-black text-white'
              }`}
            >
              <div className="p-2.5 rounded-xl bg-white/10 text-white mt-0.5">
                <Smartphone className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold block text-white">{t.applePay}</span>
                <span className="text-[10px] text-zinc-400 block mt-0.5 leading-relaxed">{t.paymentOptionSubtitle2}</span>
              </div>
              <span className="text-base font-black self-center tracking-tighter"> Pay</span>
            </button>

            <button
              type="button"
              onClick={() => handleSelectOption('google')}
              className={`w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all hover:scale-[1.01] cursor-pointer ${
                isDark 
                  ? 'border-zinc-800 hover:border-zinc-300 bg-zinc-900 text-zinc-100' 
                  : 'border-zinc-100 hover:border-zinc-300 bg-white text-stone-900'
              }`}
            >
              <div className="p-2.5 rounded-xl bg-zinc-500/5 mt-0.5">
                <span className="text-base font-black tracking-tighter block text-zinc-900 dark:text-white">G Pay</span>
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold block">{t.googlePay}</span>
                <span className="text-[10px] text-zinc-400 block mt-0.5 leading-relaxed">{t.paymentOptionSubtitle3}</span>
              </div>
              <span className="text-[10px] font-mono font-bold text-emerald-500 self-center">G Pay</span>
            </button>

            <button
              type="button"
              onClick={() => handleSelectOption('privat')}
              className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-emerald-500/20 text-left bg-emerald-500/5 text-emerald-800 dark:text-emerald-400 hover:bg-emerald-500/10 transition-all hover:scale-[1.01] cursor-pointer"
            >
              <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 mt-0.5">
                <span className="text-sm font-black">P24</span>
              </div>
              <div className="flex-1">
                <span className="text-xs font-bold block text-emerald-700 dark:text-emerald-400">{t.privat24}</span>
                <span className="text-[10px] text-zinc-400 block mt-0.5 leading-relaxed">{t.paymentOptionSubtitle4}</span>
              </div>
              <span className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400 self-center">Privat24</span>
            </button>
          </div>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onPaymentCancel}
              className="w-full py-3 rounded-xl border border-zinc-200 dark:border-zinc-850 hover:bg-zinc-50 dark:hover:bg-zinc-900 text-zinc-500 font-semibold transition-colors cursor-pointer text-center text-xs"
            >
              {t.cancel}
            </button>
          </div>
        </div>
      )}

      {step === 'verifying' && (
        <div className="py-6 flex flex-col items-center justify-center space-y-5">
          <div className="w-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 p-4 rounded-2xl flex items-start gap-3">
            <div className="p-1.5 rounded-lg bg-amber-500/20 mt-0.5 shrink-0">
              <Shield className="w-4 h-4 text-amber-500 animate-pulse" />
            </div>
            <div className="flex-1 space-y-1">
              <h5 className="text-xs font-bold uppercase tracking-wider">{getHelperText('newTabOpened')}</h5>
              <p className="text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                {getHelperText('newTabInstructions').replace('{amount}', activePrice.toLocaleString())}
              </p>
            </div>
          </div>

          <a
            href={gatewayUrl || `https://www.liqpay.ua/${lang === 'uk' ? 'uk' : 'en'}/checkout/i36552116639?amount=${activePrice || totalPrice}&currency=UAH&description=${encodeURIComponent(`Hostel Cozzydine Booking #${activeBookingId || ''} for ${guestName}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-4 px-6 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm tracking-wide shadow-lg shadow-amber-500/20 transition-all text-center flex items-center justify-center gap-2 hover:scale-[1.01] cursor-pointer no-underline border-0 outline-none"
          >
            <CreditCard className="w-4 h-4" />
            <span>
              {getHelperText('btnOpenGateway')} (₴{(activePrice || totalPrice).toLocaleString()})
            </span>
          </a>

          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-500 font-mono animate-pulse">
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-500" />
            <span>{getHelperText('waitingPaymentConfirm')}</span>
          </div>

          <div className="w-full border-t border-zinc-150/50 dark:border-zinc-850 pt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={handleManualCheck}
              className="w-full py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900 text-xs font-semibold transition-colors cursor-pointer"
            >
              {getHelperText('btnVerifyManual')}
            </button>

            {manualCheckError && (
              <p className="text-[10px] text-red-500 dark:text-red-400 text-center font-semibold font-sans mt-1 animate-pulse">
                {manualCheckError}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                setStep('select');
                setActiveBookingId(null);
                setManualCheckError(null);
              }}
              className="text-[11px] text-zinc-400 hover:text-zinc-600 text-center underline font-medium cursor-pointer mt-1 bg-transparent border-0 outline-none"
            >
              {getHelperText('changeMethod')}
            </button>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="py-12 flex flex-col items-center justify-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 animate-bounce" />
          </div>
          <div className="text-center">
            <p className="font-bold text-base text-zinc-800 dark:text-zinc-200">{t.verifySuccess}</p>
            <p className="text-xs text-zinc-400 mt-1">{t.redirecting}</p>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-center gap-1.5 text-[9.5px] text-zinc-400 font-mono border-t border-zinc-150/50 dark:border-zinc-850 pt-4">
        <Lock className="w-3 h-3 text-emerald-500" />
        <span>{t.secureNotice}</span>
      </div>
    </div>
  );
}

export default LiqPayInlineTerminal;
